// SAST business-date helpers shared across web features (spec §4 item 7, Amendment
// 2026-07-10). The plants run on Africa/Johannesburg time — a FIXED UTC+2 offset
// year-round (South Africa observes no daylight saving).
//
// The day-boundary math itself lives in `@zebra/core/src/time.ts` and is re-exported
// here rather than duplicated — the web already value-imports core constants
// (`PRICING_BASES`, `VAT_TREATMENTS`, ...) so this adds no new runtime coupling, and
// one pinned implementation beats three drifting copies (sales-invoicing and
// stores-transfers each used to carry their own UTC-buggy `todayIsoDate`).
//
// WHY THIS EXISTS (the bug class it closes): `YYYY-MM-DD` strings from date inputs
// used to be coerced through bare `new Date(dateStr)` / read back through
// `toISOString().slice(0, 10)` — both of which use the UTC calendar, so a boundary
// meant as "midnight SAST" landed at 02:00 SAST and a night-shift user's "today"
// was yesterday for the first two SAST hours of every day.
import { formatSastDate as coreFormatSastDate } from '@zebra/core';

export { sastDayStart, sastDayEnd } from '@zebra/core';

/** Renders an instant as its SAST calendar date, `'YYYY-MM-DD'` — the inverse of
 * `sastDayStart`/`sastDayEnd`, for prefilling a stored timestamp back into a
 * `type="date"` form field. Accepts the wire's ISO string form directly (core's
 * `formatSastDate` takes a `Date`). */
export function formatSastDate(value: Date | string): string {
  return coreFormatSastDate(value instanceof Date ? value : new Date(value));
}

/** Today's SAST calendar date, `'YYYY-MM-DD'` — the default value for document-date
 * inputs. NOT `new Date().toISOString().slice(0, 10)` (the UTC date), which is
 * yesterday's date for the first two SAST hours of every day. */
export function todayIsoDate(): string {
  return coreFormatSastDate(new Date());
}
